import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import StarsIcon from '@material-ui/icons/Stars';
import Alert from '@material-ui/lab/Alert';
import useApi from '../../../utils/useApi';

const useStyles = makeStyles((theme) => ({
  duxiaButton: {
    color: '#17497F',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: 6,
    padding: '4px 12px',
    fontSize: '0.8rem',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(23, 73, 127, 0.08)',
    },
    '&:disabled': {
      color: '#94a3b8',
    },
  },
  dialogTitle: {
    fontWeight: 700,
    color: '#17497F',
  },
  dialogBtnPrimary: {
    color: '#17497F',
    fontWeight: 600,
  },
  dialogBtnSecondary: {
    color: '#e74c3c',
  },
}));

export default function UIDuxiaDescriptionButton({
  itemName = '',
  categoryName = '',
  itemType = 'produto',
  currentDescription = '',
  onGenerated,
}) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const [descriptionApi] = useApi({
    method: 'post',
    url: '/duxia/agents/item-description',
  });

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleButtonClick = () => {
    if (!itemName || !itemName.trim()) {
      showToast(`Informe o nome do ${itemType} para que a DuxIA possa gerar a descrição.`, 'warning');
      return;
    }

    if (currentDescription && currentDescription.trim().length > 0) {
      setOpenConfirmDialog(true);
    } else {
      executeGenerate('new');
    }
  };

  const executeGenerate = (mode) => {
    setOpenConfirmDialog(false);
    setLoading(true);

    const payload = {
      name: itemName.trim(),
      category: categoryName ? (typeof categoryName === 'object' ? categoryName.name : categoryName) : null,
      type: itemType,
      current_description: currentDescription || null,
      mode: mode,
    };

    descriptionApi({
      data: payload,
    })
      .then((res) => {
        setLoading(false);
        if (res && res.data && res.data.description) {
          if (onGenerated) {
            onGenerated(res.data.description);
          }
          showToast('Descrição gerada com sucesso pela DuxIA!', 'success');
        } else if (res && res.data && res.data.error) {
          showToast(res.data.error, 'error');
        } else {
          showToast('Erro ao gerar descrição com a DuxIA.', 'error');
        }
      })
      .catch((err) => {
        setLoading(false);
        const errMsg = err?.response?.data?.error || 'Erro ao se comunicar com a DuxIA.';
        showToast(errMsg, 'error');
      });
  };

  return (
    <>
      <Tooltip title={`Gerar descrição comercial com IA para este ${itemType}`}>
        <Button
          size="small"
          className={classes.duxiaButton}
          onClick={handleButtonClick}
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <StarsIcon style={{ color: '#FFD700', fontSize: 16 }} />
            )
          }
        >
          {loading ? 'Gerando...' : 'Gerar com DuxIA'}
        </Button>
      </Tooltip>

      {/* Confirmation Dialog when description is not empty */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        aria-labelledby="duxia-confirm-title"
        aria-describedby="duxia-confirm-description"
      >
        <DialogTitle id="duxia-confirm-title" className={classes.dialogTitle}>
          ✨ DuxIA - Gerador de Descrição
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="duxia-confirm-description">
            O campo de descrição já contém um texto. Como você gostaria que a DuxIA processe este campo?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', flexWrap: 'wrap', gap: 8 }}>
          <Button onClick={() => setOpenConfirmDialog(false)} color="default">
            Cancelar
          </Button>
          <Button
            onClick={() => executeGenerate('refine')}
            variant="outlined"
            className={classes.dialogBtnPrimary}
          >
            Aprimorar rascunho atual
          </Button>
          <Button
            onClick={() => executeGenerate('new')}
            variant="contained"
            color="primary"
            style={{ backgroundColor: '#17497F' }}
          >
            Gerar nova do zero
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
